/**
 * Afrikaans: Templates
 *
 * What this file covers: WhatsApp message templates and Meta's approval of them.
 *
 * Tone: Precise. Keep Meta's terms consistent (template, category, approved, rejected, paused). Explain Meta's
 * rules plainly, without blame.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const templates: Catalog = {
  'templates.title': 'Sjablone',
  'templates.view.overview.emptyBody': 'Nog geen teks nie',
  'templates.view.overview.metaStatus': 'Meta-status',
  'templates.view.overview.metaNeverSeen': 'Meta het dit nog nie gesien nie',
  'templates.view.overview.importedAt': 'Ingevoer vanaf Meta',
  'templates.view.overview.placeholderLabel': 'Plekhouer {{position}}, word vervang wanneer jy stuur',
  'templates.view.variables.detected': '{{count}} plekhouers gevind',
  'templates.view.variables.label': 'Plekhouer {{position}}',
  'templates.view.variables.placeholder': 'Voorbeeldwaarde vir plekhouer {{position}}',
  'templates.columns.code': 'Kode',
  'templates.actions.for': 'Aksies vir {{code}}',
  'templates.actions.submit': 'Dien by Meta in',
  'templates.actions.duplicate': 'Dupliseer',
  'templates.actions.importFromMeta': 'Voer in vanaf Meta',
  'templates.search.label': 'Soek sjablone',
  'templates.search.placeholder': 'Soek kode, taal of kategorie',
  'templates.empty.hint': 'ŉ Sjabloon is ŉ boodskap wat Meta vooraf goedgekeur het. Jy het een nodig voordat jy kan uitsaai.',
  'templates.emptyFiltered': 'Geen sjablone pas nie',
  'templates.emptyFiltered.hint': 'Probeer ŉ ander status of soekterm.',
  'templates.status.draft': 'Konsep',
  'templates.status.submitted': 'By Meta',
  'templates.status.approved': 'Goedgekeur',
  'templates.status.rejected': 'Afgekeur',
  'templates.status.paused': 'Onderbreek',
  'templates.category.utility': 'Nut',
  'templates.category.marketing': 'Bemarking',
  'templates.category.authentication': 'Verifikasie',
  'templates.devFallback': 'Nie werklik goedgekeur nie',
  'templates.devFallback.tooltip': 'Outomaties goedgekeur deur hierdie omgewing, nie deur Meta nie. Dit sal misluk by ŉ werklike stuur.',
  'templates.metaStatus.tooltip': 'Meta rapporteer dit tans as {{status}}.',
  'templates.drawer.new': 'Nuwe sjabloon',
  'templates.drawer.edit': 'Wysig sjabloon',
  'templates.drawer.components': 'Komponente',
  'templates.drawer.preview': 'Voorskou',
  'templates.drawer.previewEmpty': 'Voeg ŉ BODY-komponent by om die boodskap hier te sien.',
  'templates.drawer.format': 'Netjies maak',
  'templates.drawer.languageLocked': 'Die taal is vas sodra ŉ sjabloon bestaan. Dupliseer dit om ŉ ander taal te maak.',
  'templates.drawer.marketingHint': 'Bemarkingsjablone mag nie ŉ kaal skakel bevat nie. Gebruik ŉ URL-knoppie.',
  'templates.modal.submit.title': 'Stuur dit vir goedkeuring na Meta?',
  'templates.modal.submit.body': '{{code}} gaan in Meta se hersieningstou, wat gewoonlik ŉ dag of langer neem.',
  'templates.modal.submit.irreversible': 'Dit kan nie gewysig word terwyl dit by Meta is nie. As hulle dit afkeur kan jy dit regmaak en weer indien.',
  'templates.toast.submittedNamed': '{{code}} is vir goedkeuring na Meta gestuur.',
  'templates.toast.deleted': '{{code}} geskrap.',
  'templates.toast.deleteFailed': 'Skrap het misluk',
  'templates.toast.imported': '{{imported}} ingevoer, {{updated}} opgedateer, {{skipped}} oorgeslaan.',
  'templates.toast.importFailed': 'Invoer het misluk',
  'templates.import.noWaba': 'Nog geen WhatsApp Business-rekening aangeheg nie. Heg eers ŉ nommer aan.',
  'templates.import.notConfigured': 'Meta-geloofsbriewe is nie opgestel nie. ŉ Administrateur voeg hulle by onder Instellings → Integrasies.',
  'templates.import.metaError': 'Meta het die versoek geweier. Kontroleer dat die geloofsbriewe aktueel is.',
  'templates.validate.title': 'Meta sal dit so afkeur',
  'templates.validate.invalidJson': 'Die komponente is nie geldige JSON nie.',
  'templates.validate.notArray': 'Komponente moet ŉ lys wees.',
  'templates.validate.bodyMissing': 'ŉ Sjabloon het ŉ BODY-komponent nodig.',
  'templates.validate.repeated': 'ŉ Sjabloon mag net een {{type}}-komponent hê.',
  'templates.validate.headerTooLong': 'Die opskrif is {{length}} karakters; Meta laat {{max}} toe.',
  'templates.validate.bodyTooLong': 'Die teks is {{length}} karakters; Meta laat {{max}} toe.',
  'templates.validate.footerTooLong': 'Die voetskrif is {{length}} karakters; Meta laat {{max}} toe.',
  'templates.validate.startsWithVariable': 'Die teks mag nie met ŉ plekhouer begin nie. Sit teks daarvoor.',
  'templates.validate.endsWithVariable': 'Die teks mag nie op ŉ plekhouer eindig nie. Sit teks daarna.',
  'templates.validate.marketingUrl': 'ŉ Bemarkingsjabloon mag nie ŉ kaal skakel in sy teks hê nie. Gebruik eerder ŉ URL-knoppie.',
  'templates.validate.notFromOne': 'Plekhouers moet by {{1}} begin.',
  'templates.validate.notContiguous': 'Plekhouers moet 1, 2, 3 loop sonder gapings; {{missing}} ontbreek.',
  'templates.toast.saved': 'Sjabloon {{code}} gestoor.',
  'templates.actions.saveDraft': 'Stoor konsep',
  'templates.columns.category': 'Kategorie',
  'templates.columns.note': 'Nota',
  'templates.description': 'WhatsApp-boodskapsjablone. Stel ’n sjabloon op, dien dit by Meta vir goedkeuring in, en gebruik dit dan in uitsendings. Goedgekeurde sjablone is onveranderlik.',
  'templates.drawer.codeHint': 'Uniek per taal. Kleinletters + onderstrepe.',
  'templates.drawer.codeRequired': 'Kode is verpligtend.',
  'templates.drawer.componentsHint': 'Skikking van { type, format?, text, buttons? }. {{1}}-plekhouers word met stuurtyd ingevul.',
  'templates.drawer.componentsInvalid': 'Komponente moet geldige JSON wees.',
  'templates.empty': 'Nog geen sjablone nie.',
  'templates.loadError': 'Kon nie sjablone laai nie.',
  'templates.modal.delete.body': 'Skrap sjabloon {{code}}? Dit kan nie ongedaan gemaak word nie.',
  'templates.modal.delete.title': 'Skrap sjabloon?',
  'templates.toast.submitFailed': 'Indiening het misluk',
  'templates.toast.submitted': 'By Meta vir hersiening ingedien.',
  'templates.view.backToAll': 'Alle sjablone',
  'templates.view.components.copied': 'Komponente-JSON gekopieer.',
  'templates.view.lifecycle.draftedAt': 'Opgestel',
  'templates.view.lifecycle.metaId': 'Meta-sjabloon-id',
  'templates.view.lifecycle.metaName': 'Meta-sjabloonnaam',
  'templates.view.lifecycle.metaSync': 'Meta-sinchronisasie',
  'templates.view.lifecycle.modal.submit.body': 'Sodra dit ingedien is, gaan hierdie sjabloon in Meta se hersieningstou in. Goedkeuring neem gewoonlik 24 uur, maar kan langer duur vir bemarkingsjablone.',
  'templates.view.lifecycle.modal.submit.title': 'Dien vir Meta-goedkeuring in?',
  'templates.view.lifecycle.submitFailed': 'Kon nie sjabloon indien nie.',
  'templates.view.lifecycle.submitHint': 'Dien hierdie sjabloon vir Meta-hersiening in. Goedgekeurde sjablone kan in uitsendings gebruik word.',
  'templates.view.lifecycle.submittedAt': 'By Meta ingedien',
  'templates.view.lifecycle.title': 'Meta-hersiening',
  'templates.view.notFound': 'Sjabloon nie gevind nie.',
  'templates.view.overview.metadata': 'Metadata',
  'templates.view.overview.preview': 'Voorskou',
  'templates.view.overview.previewHint': 'Die naaste weergawe aan wat Meta sal aflewer. Veranderlike-posisies word in amber uitgelig.',
  'templates.view.tabs.components': 'Komponente',
  'templates.view.tabs.variables': 'Veranderlikes',
  'templates.view.variables.copied': 'Ingevulde teks na knipbord gekopieer.',
  'templates.view.variables.filled': 'Ingevulde voorskou',
  'templates.view.variables.hint': 'Voer voorbeeldwaardes in om te sien hoe die weergegewe teks lyk. Waardes bly aan die kliëntkant.',
  'templates.view.variables.none': 'Hierdie sjabloon het geen {{N}}-veranderlikes nie: dit stuur ’n statiese teks.',
  'templates.banner': 'Sjablone moet deur Meta goedgekeur word voordat ’n uitsending hulle kan gebruik.',
  'templates.actions.create': 'Skep sjabloon',
  'templates.banner.rejected': '{{count}} sjabloon(e) is afgekeur. Die notakolom verduidelik waarom.',
  'templates.banner.rejected.action': 'Wys hulle',
  'templates.filters.status.any': 'Alle statusse ({{count}})',
  'templates.showingOf': 'Wys {{count}} van {{total}} sjablone',
  'templates.note.devFallback': 'Deur die ontwikkelaar-terugval goedgekeur, nie deur Meta nie.',
  'templates.note.underReview': 'Onder hersiening by Meta.',
  'templates.note.paused': 'Deur Meta gepouseer vir gehalte.',
  'templates.import.title': 'Voer sjablone vanaf Meta in',
  'templates.import.body': 'Trek die sjablone wat reeds op jou WhatsApp Business-rekening bestaan en hou hulle in sinkronisasie.',
  'templates.view.overview.businessName': 'Jou besigheid',
  'templates.view.overview.sampleTime': '11:30',
  'templates.view.overview.composer': 'Tik ’n boodskap',
  'templates.header.label': 'Kopstuk',
  'templates.header.hint': '’n Sjabloon kan een kopstuk hê: ’n reël teks, of ’n prent, video of dokument.',
  'templates.header.format.none': 'Geen kopstuk',
  'templates.header.format.text': 'Teks',
  'templates.header.format.image': 'Prent',
  'templates.header.format.video': 'Video',
  'templates.header.format.document': 'Dokument',
  'templates.header.text': 'Kopstukteks',
  'templates.header.text.hint': 'Tot 60 karakters. Een veranderlike word toegelaat.',
  'templates.header.sample': 'Voorbeeld-media-URL',
  'templates.header.sample.hint': 'Meta hersien hierdie voorbeeld. ’n Media-sjabloon sonder een word afgekeur.',
  'templates.header.sendHint': 'Hierdie voorbeeld is net vir goedkeuring. Elke uitsending verskaf sy eie media wanneer dit stuur.',
  'templates.header.noSample': 'Nog geen voorbeeldmedia nie',
  'templates.validate.headerNoSample': '’n {{format}}-kopstuk het ’n voorbeeld-media-URL nodig: Meta keur die sjabloon sonder een af.',
  'templates.validate.headerMediaWithText': '’n Media-kopstuk kan nie ook teks dra nie. Verwyder die teks, of verander die kopstuk na Teks.',
  'templates.validate.headerTextEmpty': '’n Teks-kopstuk het teks nodig.',
  'templates.ways.library': 'Begin uit die biblioteek',
  'templates.ways.library.body': 'Kies \'n boodskap wat reeds geskryf is: bestellingbevestigings, aflewerings, afspraak- en betalingsherinnerings. Jy kan die bewoording daarna verander as jy wil.',
  'templates.ways.library.action': 'Blaai deur die biblioteek',
  'templates.ways.import': 'Bring in wat jy reeds het',
  'templates.ways.import.body': 'As jou WhatsApp-rekening reeds goedgekeurde sjablone het, kopieer hulle hierheen in plaas daarvan om hulle weer te tik. Niks op WhatsApp verander nie.',
  'templates.ways.blank': 'Skryf jou eie',
  'templates.ways.blank.body': 'Begin van voor af en skryf die boodskap self, met \'n voorskou van die selfoon langs jou terwyl jy tik.',
  'templates.ways.blank.action': 'Begin skryf',
  'templates.wizard.title.new': 'Nuwe sjabloon',
  'templates.wizard.title.edit': 'Wysig sjabloon',
  'templates.wizard.description.new': 'Vier stappe: waar om te begin, wat om dit te noem, wat dit sê, en een laaste kyk voordat WhatsApp dit sien.',
  'templates.wizard.description.edit': 'Net \'n konsep of \'n afgekeurde sjabloon kan verander word. Sodra WhatsApp dit goedkeur, is die bewoording vas.',
  'templates.wizard.step.start': 'Begin',
  'templates.wizard.step.start.hint': 'Biblioteek, invoer of leeg',
  'templates.wizard.step.basics': 'Basiese',
  'templates.wizard.step.basics.hint': 'Naam, taal, kategorie',
  'templates.wizard.step.content': 'Inhoud',
  'templates.wizard.step.content.hint': 'Die boodskap self',
  'templates.wizard.step.review': 'Nagaan',
  'templates.wizard.step.review.hint': 'Kyk, dan stuur',
  'templates.wizard.back': 'Terug',
  'templates.wizard.next': 'Volgende',
  'templates.wizard.way.library': 'Begin uit die biblioteek',
  'templates.wizard.way.library.body': 'Boodskappe wat reeds geskryf is, en in WhatsApp se geval reeds nagegaan is, so goedkeuring is gewoonlik vinniger.',
  'templates.wizard.way.library.action': 'Blaai',
  'templates.wizard.way.import': 'Bring in wat jy reeds het',
  'templates.wizard.way.import.body': 'Kopieer die sjablone wat reeds op jou WhatsApp-rekening is hierheen. Niks op WhatsApp verander nie.',
  'templates.wizard.way.import.action': 'Voer nou in',
  'templates.wizard.way.blank': 'Skryf jou eie',
  'templates.wizard.way.blank.body': 'Begin van voor af, met \'n voorskou van die selfoon langs jou terwyl jy tik.',
  'templates.wizard.way.blank.action': 'Begin skryf',
  'templates.wizard.name': 'Naam',
  'templates.wizard.name.hint': 'Net kleinletters, syfers en onderstrepe. Dit is hoe jy na die sjabloon verwys wanneer jy dit stuur.',
  'templates.wizard.name.willBecome': 'Dit word gestoor as {{name}}, want WhatsApp laat net kleinletters, syfers en onderstrepe toe.',
  'templates.wizard.name.ok': 'Dit is \'n naam wat WhatsApp sal aanvaar.',
  'templates.wizard.name.required': 'Gee die sjabloon \'n naam.',
  'templates.wizard.language.hint': 'WhatsApp berg \'n sjabloon onder sy naam en taal saam, en nie een van die twee kan daarna verander word nie. Dupliseer die sjabloon om \'n tweede taal by te voeg.',
  'templates.wizard.language.locked': 'Die taal kan nie verander word nie. Dupliseer hierdie sjabloon om \'n weergawe in \'n ander taal te maak.',
  'templates.wizard.category.hint': 'Die kategorie bepaal wat elke boodskap kos en hoe streng WhatsApp dit nagaan. Om bemarking vir \'n kwitansie te kies, of nut vir \'n advertensie, laat die sjabloon afgekeur word.',
  'templates.wizard.category.utility.what': 'Iets wat die kliënt gevra het of verwag: \'n bestellingbevestiging, \'n aflewering, \'n herinnering oor \'n bespreking.',
  'templates.wizard.category.utility.cost': 'Goedkoopste om te stuur',
  'templates.wizard.category.marketing.what': 'Enigiets promosioneel: \'n spesiale aanbod, \'n nuwe item, \'n boodskap wat iemand vra om terug te kom.',
  'templates.wizard.category.marketing.cost': 'Kos meer, en die kliënt moet ingestem het',
  'templates.wizard.category.authentication.what': 'Eenmalige kodes om aan te meld of \'n betaling te bevestig. Niks anders nie.',
  'templates.wizard.category.authentication.cost': 'Word apart geprys',
  'templates.wizard.category.authentication.warning': '\'n Verifikasiesjabloon mag net \'n eenmalige kode en die woorde daarom bevat. WhatsApp keur enigiets af wat in hierdie kategorie soos \'n gewone boodskap lyk.',
  'templates.wizard.content.title': 'Die boodskap',
  'templates.wizard.content.editJson': 'Wysig as JSON',
  'templates.wizard.content.editForm': 'Terug na die vorm',
  'templates.wizard.content.json': 'Komponente',
  'templates.wizard.content.jsonHint': 'WhatsApp se eie komponentformaat. Wat ook al hier is, is presies wat vir nagaan gestuur word.',
  'templates.wizard.content.advancedOnly': 'Hierdie sjabloon het dele wat die vorm nie kan wys nie, so dit bly in die JSON-wysiger. So gaan niks verlore nie.',
  'templates.wizard.header.sample': 'Voorbeeld vir die opskrif',
  'templates.wizard.header.sample.hint': 'Wat WhatsApp in die plek van die plekhouer moet sien wanneer dit die opskrif nagaan.',
  'templates.wizard.body': 'Boodskap',
  'templates.wizard.body.hint': 'Hou dit kort en sê wie jy is. WhatsApp aanvaar nie \'n boodskap wat met \'n plekhouer begin of eindig nie.',
  'templates.wizard.body.placeholder': 'Hallo, dankie vir jou bestelling.',
  'templates.wizard.count': '{{length}} van {{max}}',
  'templates.wizard.addPlaceholder': 'Voeg \'n plekhouer by',
  'templates.wizard.samples': 'Voorbeeldwaardes',
  'templates.wizard.samples.hint': 'WhatsApp lees die sjabloon met hierdie in die plek van die plekhouers wanneer dit nagegaan word, so gebruik voorbeelde wat eg lyk.',
  'templates.wizard.samples.for': 'Voorbeeld vir plekhouer {{n}}',
  'templates.wizard.samples.placeholder': 'Byvoorbeeld: Thabo',
  'templates.wizard.footer': 'Voetskrif',
  'templates.wizard.footer.hint': '\'n Klein reël onder die boodskap, hoogstens 60 karakters. Plekhouers word hier nie toegelaat nie.',
  'templates.wizard.buttons': 'Knoppies',
  'templates.wizard.buttons.hint': 'Knoppies is die verskil tussen \'n boodskap wat mense lees en een waarop hulle reageer. Voeg tot tien by.',
  'templates.wizard.buttons.count': '{{count}} van {{max}}',
  'templates.wizard.buttons.type': 'Doen wat',
  'templates.wizard.buttons.type.quick_reply': 'Antwoord met \'n vasgestelde antwoord',
  'templates.wizard.buttons.type.url': 'Maak \'n skakel oop',
  'templates.wizard.buttons.type.phone_number': 'Bel \'n nommer',
  'templates.wizard.buttons.label': 'Etiket',
  'templates.wizard.buttons.label.hint': 'Wat die kliënt op die knoppie sien. Hoogstens 25 karakters.',
  'templates.wizard.buttons.url': 'Skakel',
  'templates.wizard.buttons.url.hint': 'Die volle adres, wat met https:// begin. Sit {{1}} aan die einde as die laaste deel per boodskap verskil.',
  'templates.wizard.buttons.phone': 'Nommer om te bel',
  'templates.wizard.buttons.phone.hint': 'In volle internasionale vorm, byvoorbeeld +27 82 123 4567.',
  'templates.wizard.buttons.remove': 'Verwyder hierdie knoppie',
  'templates.wizard.buttons.add.quick_reply': 'Voeg \'n antwoordknoppie by',
  'templates.wizard.buttons.add.url': 'Voeg \'n skakelknoppie by',
  'templates.wizard.buttons.add.phone_number': 'Voeg \'n belknoppie by',
  'templates.wizard.preview.business': 'Jou besigheid',
  'templates.wizard.preview.composer': 'Boodskap',
  'templates.wizard.preview.empty': 'Jou boodskap verskyn hier.',
  'templates.wizard.preview.hint': 'Dit is wat die kliënt sien, met jou voorbeeldwaardes ingevul.',
  'templates.wizard.review.summary': 'Wat jy stuur',
  'templates.wizard.review.clean': 'Niks hier sal afgekeur word nie. Jy kan dit as \'n konsep stoor of dit nou na WhatsApp stuur.',
  'templates.wizard.review.problems': '{{count}} om reg te maak voordat dit gestuur kan word',
  'templates.wizard.review.advice': 'Die moeite werd om reg te maak, maar dit keer nie die sjabloon nie',
  'templates.wizard.review.metaLibrary': 'Dit is WhatsApp se eie bewoording, onveranderd, so dit gaan gewoonlik baie vinniger deur nagaan as \'n sjabloon wat jy self skryf.',
  'templates.wizard.review.submitWarning': 'Om dit na WhatsApp te stuur kan nie ongedaan gemaak word nie. Die bewoording is van daardie oomblik af vas en nagaan neem gewoonlik omtrent \'n dag.',
  'templates.wizard.leave.title': 'Vertrek sonder om te stoor?',
  'templates.wizard.leave.body': 'Hierdie sjabloon is nie gestoor nie. As jy nou vertrek, gaan alles wat jy getik het verlore.',
  'templates.wizard.leave.discard': 'Vertrek en verloor dit',
  'templates.wizard.leave.keep': 'Bly hier',
  'templates.library.search.label': 'Soek',
  'templates.library.search.placeholder': 'Bestelling, aflewering, betaling…',
  'templates.library.category.any': 'Enige kategorie',
  'templates.library.loadError': 'Die biblioteek kon nie gelaai word nie. Probeer oor \'n oomblik weer.',
  'templates.library.metaUnavailable': 'WhatsApp bied nie sy eie biblioteek aan hierdie rekening nie, so wat jy hieronder sien is deur ons geskryf. Dit werk presies dieselfde.',
  'templates.library.empty': 'Niks pas nie',
  'templates.library.empty.hint': 'Probeer \'n ander woord, of maak die kategorie skoon.',
  'templates.library.source.meta': 'WhatsApp',
  'templates.library.source.starter': 'RepletoryReach-beginpunt',
  'templates.library.use': 'Gebruik hierdie een',
  'templates.library.chosen': 'Gekies',
  'templates.validate.buttonsEmpty': 'Daar is \'n knoppie-afdeling sonder knoppies daarin. Voeg een by, of verwyder die afdeling.',
  'templates.validate.buttonsTooMany': 'Daar is {{count}} knoppies; WhatsApp laat {{max}} toe.',
  'templates.validate.buttonBadType': '\'{{type}}\' is nie iets wat \'n knoppie kan doen nie.',
  'templates.validate.buttonTextEmpty': 'Elke knoppie het \'n etiket nodig.',
  'templates.validate.buttonTextTooLong': 'Die knoppie-etiket \'{{text}}\' is {{length}} karakters; WhatsApp laat {{max}} toe.',
  'templates.validate.buttonUrlMissing': '\'n Skakelknoppie het geen adres nie. Voeg die adres by wat dit moet oopmaak.',
  'templates.validate.buttonPhoneMissing': '\'n Belknoppie het geen nommer nie. Voeg die nommer by wat dit moet bel.',
  'templates.validate.buttonUrlRepeated': 'Daar is {{count}} skakelknoppies; WhatsApp laat {{max}} toe.',
  'templates.validate.buttonPhoneRepeated': 'Daar is {{count}} belknoppies; WhatsApp laat {{max}} toe.',
  'templates.validate.nameEmpty': 'Die sjabloon het \'n naam nodig.',
  'templates.validate.nameTooLong': 'Die naam is {{length}} karakters; WhatsApp laat {{max}} toe.',
  'templates.validate.nameIllegal': '\'n Naam mag net kleinletters, syfers en onderstrepe bevat.',
  'templates.validate.samplesMissing': '{{have}} van die {{wanted}} plekhouers het \'n voorbeeldwaarde. WhatsApp gaan die sjabloon teen die voorbeelde na, so vul vir elkeen \'n egte voorbeeld in.',
  'templates.wizard.name.taken': 'Jy het reeds \'n sjabloon met hierdie naam in hierdie taal. Gee hierdie een \'n ander naam.',
  'templates.validate.buttonUrlPlaceholder': '\'n Skakelknoppie wys steeds na example.com. Sit jou eie adres in voordat jy dit vir nagaan stuur.'
};

export default templates;
